import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { optionName, readField } from '@aid/shared/utils/object-fields';

@Component({
  selector: 'aid-select',
  template: `
    <mat-form-field
      class="full-width"
      [appearance]="appearance"
      [matTooltip]="tooltip"
      [matTooltipDisabled]="!tooltip"
    >
      <mat-label>
        {{ label | translate | sentancecase }}
      </mat-label>

      @if (loading) {
        <mat-spinner class="filter__loading" diameter="16"> </mat-spinner>
      }
      <mat-select
        [formControl]="control"
        [multiple]="multiple"
        [required]="isRequired"
      >
        @if (filterable) {
          <div class="filter filter--sticky aid-layout-row">
            <mat-icon class="filter--margin" svgIcon="search"></mat-icon>
            <input
              matInput
              placeholder="{{ 'filter' | translate | titlecase }}"
              type="text"
              autocomplete="off"
              [formControl]="filterControl"
              (keydown.Space)="$event.stopPropagation()"
              (blur)="onBlur()"
            />
          </div>
        }
        @if (hasValue && clear) {
          <button (click)="onClear()" mat-button class="filter__clear">
            {{ 'clear' | translate | uppercase }}
          </button>
        }
        @if (!options || options.length === 0) {
          <mat-option>
            {{ 'no-items-found' | translate | sentancecase }}
          </mat-option>
        }
        @if (filteredItems$ | async; as filteredItems) {
          @if (options && options.length !== 0 && filteredItems.length === 0) {
            <mat-option>
              {{ 'no-results-clear-filter' | translate | sentancecase }}
            </mat-option>
          }
          @for (item of filteredItems; track item) {
            <mat-option
              [value]="optionValue(item)"
              [disabled]="optionDisabled(item)"
              data-cy="option-select"
            >
              @if (translate) {
                <span>
                  {{ displayFn(item) | translate | sentancecase }}
                </span>
              }
              @if (!translate) {
                <span> {{ displayFn(item) }} </span>
              }
              @if (noteFn(item)) {
                <small style="margin-left:16px">
                  <strong>
                    {{ noteFn(item) | translate | sentancecase }}
                  </strong>
                </small>
              }
            </mat-option>
          }
        }
      </mat-select>
      @if (control.hasError('required')) {
        <mat-error>
          {{ label || 'this-field' | translate | titlecase }}
          {{ 'is-required' | translate }}
        </mat-error>
      }

      @for (error of control.errors | keyvalue; track error) {
        <mat-error>
          @if (error.key !== 'required') {
            {{ error.value | translate }}
          }
        </mat-error>
      }
    </mat-form-field>
  `,
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SelectComponent<T = unknown> implements OnInit {
  @Input() selectFirst = false;
  @Input({ required: true }) control = new UntypedFormControl();
  @Input('options') set items(options: T[] | null | undefined) {
    if (options) {
      this.initFilter(options);
      this.options = options;
      this.selectFirstOption(options);
    }
  }

  filteredItemsLength = 0;

  constructor() {}

  get isRequired(): boolean {
    if (!this.label) {
      return false;
    }

    if (this.control.validator) {
      const validator = this.control.validator({} as AbstractControl);
      if (validator && validator.required) {
        return true;
      }
    }

    return false;
  }
  filterControl: UntypedFormControl = new UntypedFormControl();
  filteredItems$?: Observable<T[]>;
  options?: T[];
  @ViewChild(MatSelect, { static: true }) matSelect?: MatSelect;

  @Output() select = new EventEmitter();

  @Input() label?: string;
  @Input() translate: boolean | string = false;
  @Input() filterable = true;
  @Input() clear = true;
  @Input() multiple?: boolean;
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() loading = false;
  @Input() tooltip: string = '';
  @Input() displayFn: (item: T) => string | null | undefined = item =>
    optionName(item);
  @Input() noteFn: (item: T) => string | undefined = item => {
    const note = readField(item, 'note');
    return typeof note === 'string' ? note : undefined;
  };

  ngOnInit() {
    this.control.valueChanges.subscribe(value => {
      this.select.emit(value);
    });
  }

  onClear() {
    this.control.setValue(null);
    if (!this.matSelect) {
      throw new Error('aid-select: the mat-select is not rendered.');
    }
    this.matSelect.close();
  }

  initFilter(options: T[]) {
    this.filteredItems$ = this.filterControl.valueChanges.pipe(
      startWith(''),
      map(filterBy => (filterBy ? this.filteredBy(filterBy) : options.slice())),
      tap(filteredItems => (this.filteredItemsLength = filteredItems.length))
    );
  }

  filteredBy(filterBy: string) {
    if (!this.options) {
      return [];
    }

    return this.options.filter(
      item =>
        (this.displayFn(item) ?? '')
          .toLowerCase()
          .indexOf(filterBy.toLowerCase()) > -1
    );
  }

  optionValue(item: T): unknown {
    return readField(item, 'id');
  }

  optionDisabled(item: T): unknown {
    return readField(item, 'disabled');
  }

  private selectFirstOption(options: T[]) {
    if (!this.selectFirst) {
      return;
    }

    if (options.length === 1) {
      this.control.setValue(this.optionValue(options[0]));
    }
  }

  get hasValue() {
    return this.control.value !== null && this.control.value !== undefined;
  }

  onBlur() {
    if (this.filteredItemsLength > 0) {
      return;
    }
    this.filterControl.setValue('');
  }
}
